import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { saveAs } from 'file-saver';
import { Loader2, Calculator, Link2, Unlink, FileText, FileSpreadsheet, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const money = (sym, v) => (v == null ? '—' : `${sym}${Math.round(Number(v)).toLocaleString()}`);

/** Phase 3 — link an estimate (implementation + product pricing) and show the client-facing Cost Summary in the price-book currency. */
export function CommercialsCard({ project, onRefresh }) {
  const pid = project.id;
  const linked = project.commercials;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [picking, setPicking] = useState(false);
  const [q, setQ] = useState('');
  const [options, setOptions] = useState([]);
  const [exporting, setExporting] = useState('');
  useEffect(() => {
    if (!linked) { setData(null); return; }
    setLoading(true);
    axios.get(`${API_URL}/api/rfp/response/projects/${pid}/commercials`, { withCredentials: true })
      .then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [pid, linked?.estimate_id, linked?.linked_at]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!picking) return;
    const t = setTimeout(() => {
      axios.get(`${API_URL}/api/estimates`, { params: { limit: 12, search: q }, withCredentials: true })
        .then(r => setOptions(r.data.estimates || [])).catch(() => setOptions([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, picking]);
  const link = async (estimate_id) => {
    try {
      await axios.put(`${API_URL}/api/rfp/response/projects/${pid}/commercials`, { estimate_id }, { withCredentials: true });
      toast.success(estimate_id ? 'Estimate linked — Cost Summary ready' : 'Estimate unlinked');
      setPicking(false); onRefresh();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not link'); }
  };
  const exp = async (fmt) => {
    setExporting(fmt);
    try {
      const r = await axios.get(`${API_URL}/api/rfp/response/projects/${pid}/commercials/export`, { params: { format: fmt }, withCredentials: true, responseType: 'blob' });
      const cd = r.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^";]+)"?/);
      saveAs(r.data, m ? m[1] : `Cost Summary.${fmt}`);
    } catch { toast.error('Export failed'); }
    finally { setExporting(''); }
  };
  const s = data?.summary;
  return (
    <div className="border border-slate-200 bg-white rounded-md p-4 space-y-3" data-testid="rfp-commercials">
      <div className="flex items-center gap-2 flex-wrap">
        <Calculator className="w-4 h-4 text-[#4ABFCF]" />
        <span className="text-sm font-semibold text-[#0F2040]">Commercials — Cost Summary</span>
        {linked ? (
          <>
            <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="rfp-commercials-linked">{linked.estimate_name}</Badge>
            <Badge variant="outline" className="text-[9px]" data-testid="rfp-commercials-ccy">{s?.currency || linked.currency} price book</Badge>
          </>
        ) : <span className="text-[10px] text-slate-500">Link the estimate that prices this RFP (implementation + product subscription)</span>}
        <span className="ml-auto flex items-center gap-1.5">
          {linked && <>
            <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={!!exporting} onClick={() => exp('docx')} data-testid="rfp-commercials-docx"><FileText className="w-3 h-3 mr-1" />{exporting === 'docx' ? '…' : 'Word'}</Button>
            <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={!!exporting} onClick={() => exp('xlsx')} data-testid="rfp-commercials-xlsx"><FileSpreadsheet className="w-3 h-3 mr-1" />{exporting === 'xlsx' ? '…' : 'Excel'}</Button>
            <Button size="sm" variant="ghost" className="h-7 text-[11px] text-slate-500" onClick={() => link(null)} data-testid="rfp-commercials-unlink"><Unlink className="w-3 h-3 mr-1" />Unlink</Button>
          </>}
          <Button size="sm" className="h-7 text-[11px] bg-[#0F2040] hover:bg-[#1A3055] text-white" onClick={() => setPicking(p => !p)} data-testid="rfp-commercials-link-btn"><Link2 className="w-3 h-3 mr-1" />{linked ? 'Change estimate' : 'Link estimate'}</Button>
        </span>
      </div>
      {picking && (
        <div className="space-y-1.5" data-testid="rfp-commercials-picker">
          <div className="relative"><Search className="w-3 h-3 absolute left-2 top-2.5 text-slate-400" /><Input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search estimates by name or client…" className="h-8 text-xs pl-7" data-testid="rfp-commercials-search" /></div>
          <div className="divide-y divide-slate-100 border border-slate-100 rounded-md max-h-56 overflow-y-auto">
            {options.length === 0 && <p className="text-[11px] text-slate-400 p-2">No estimates match.</p>}
            {options.map(e => (
              <button key={e.id} type="button" onClick={() => link(e.id)} className="w-full text-left p-2 text-xs hover:bg-slate-50 flex items-center gap-2" data-testid={`rfp-commercials-opt-${e.id}`}>
                <span className="font-medium text-[#0F2040]">{e.estimate_name}</span>
                <span className="text-slate-400">{e.organization_name}</span>
                {e.product_pricing?.results && <Badge variant="outline" className="text-[9px] ml-auto">{e.product_pricing.results.currency} · {e.product_pricing.results.package?.label} · {e.product_pricing.results.term_years}-yr</Badge>}
              </button>
            ))}
          </div>
        </div>
      )}
      {linked && loading && <p className="text-xs text-slate-400"><Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" />Pricing…</p>}
      {linked && !loading && data?.error && <p className="text-xs text-rose-600">{data.error}</p>}
      {s && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {[
              ['Product subscription (term)', money(s.symbol, s.subscription_total), s.product ? `${s.product.package} · ${s.product.term_years}-yr${s.product.term_discount_pct ? ` · ${s.product.term_discount_pct}% term disc.` : ''}` : 'No subscription priced', 'sub'],
              ['Implementation', money(s.symbol, s.implementation), s.converted ? `converted from ${s.impl_native_currency} at saved FX` : 'estimate currency', 'impl'],
              ['Total contract value', money(s.symbol, s.tcv), `${s.currency} · sell prices only`, 'tcv'],
              ['Effort & duration', `${Math.round(s.implementation_detail?.hours || 0).toLocaleString()} hrs`, `${s.implementation_detail?.component_count || 0} components${s.timeline?.total_weeks ? ` · ${s.timeline.total_weeks} weeks` : ''}`, 'effort'],
            ].map(([label, value, sub, key]) => (
              <div key={key} className="bg-slate-50 rounded-md p-2.5" data-testid={`rfp-commercials-tile-${key}`}>
                <p className="text-[10px] text-slate-500">{label}</p>
                <p className="text-lg font-bold text-[#0F2040] leading-tight">{value}</p>
                <p className="text-[10px] text-slate-400 truncate" title={sub}>{sub}</p>
              </div>
            ))}
          </div>
          {s.years?.length > 0 && (
            <table className="w-full text-[11px]" data-testid="rfp-commercials-years">
              <thead><tr className="text-slate-500 text-left"><th className="py-1 font-medium">Year</th><th className="font-medium text-right">Subscription</th><th className="font-medium text-right">Implementation</th><th className="font-medium text-right">Total</th></tr></thead>
              <tbody>
                {s.years.map(y => <tr key={y.year} className="border-t border-slate-100"><td className="py-1">Year {y.year}</td><td className="text-right">{money(s.symbol, y.subscription)}</td><td className="text-right">{y.implementation ? money(s.symbol, y.implementation) : '—'}</td><td className="text-right font-medium">{money(s.symbol, y.total)}</td></tr>)}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}
